import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AtCostFSPortfolioMetrics, AtGainFSPortfolioMetrics, AtLossFSPortfolioMetrics, FSPortfolioMetricsDTO, GroupFS, TotalFSPortfolioMetrics, WriteOffFSPortfolioMetrics } from '../../pojo/fund-strategy-export-classes';


@Component({
  selector: 'app-fund-strategy-portfolio-metrics',
  templateUrl: './fund-strategy-portfolio-metrics.component.html',
  styleUrls: ['./fund-strategy-portfolio-metrics.component.scss'],
})

export class FundStrategyPortfolioMetricsComponent implements OnChanges {

  @Input() fsGroups: GroupFS[] = [];
  @Input() isModelBlocked = false;
  @Output() totalsEmitter: EventEmitter<FSPortfolioMetricsDTO> = new EventEmitter<FSPortfolioMetricsDTO>();

  atCost: AtCostFSPortfolioMetrics;
  atGain: AtGainFSPortfolioMetrics;
  atLoss: AtLossFSPortfolioMetrics;
  writeOff: WriteOffFSPortfolioMetrics;
  total: TotalFSPortfolioMetrics;

  totalsEmitterDTO = new FSPortfolioMetricsDTO();

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isModelBlocked && changes.isModelBlocked) {
      this.fsGroups = [];
      this.calculatePortfolioData(this.fsGroups);
    }
    if (changes.fsGroups && changes.fsGroups.currentValue !== changes.fsGroups.previousValue) {
      this.calculatePortfolioData(this.fsGroups);
    }
  }

  /**
   * We iterate through all rounds in each subGroup and group and calculate the numbers of companies,
   * which were sold(exit) at gain, loss or at cost.
   *
   * gain = proceeds - investment.
   * gain > 0 company was sold at gain
   * gain < 0 company was sold at loss
   * gain = 0 company was sold at cost
   *
   * | investment | exitComp | proceeds |
   * | 1000       | 1        | 5000     | 1 comp atGain -> 5000 - 1000 > 0                          | inv = 1000       | proceed = 5000
   * | 2000       | 1        | 2200     | 1 comp atLoss -> (1000 + 2000) - 2200 < 0                 | inv = 3000       | proceed = 2200
   * | 3000       | 1        | 6000     | 1 comp atCost -> (1000 + 2000 + 3000) - 6000 = 0          | inv = 6000       | proceed = 6000
   * | 4000       | 2        | 22 000   | 2 comp atGain -> (1000 + 2000 + 3000 + 4000) - 22 000 > 0 | inv = 10 000 * 2 | proceed = 22 000 *2
   *
   * finally we have
   *                  | investment | proceed
   * atGain -> 3 comp | 21 000     | 47 000
   * atLoss -> 1 comp | 3 000      | 2200
   * atCost -> 1 comp | 6000       | 6000
   *
   * ----------------------------------------------------
   * write off means, that we sell the company by 0$.
   * | investment | writeOff |
   * | 1000       | 1        | 1 comp writeOff | inv = 1000
   * | 2000       | 3        | 3 comp writeOff | inv = (1000 + 2000) * 3
   *
   * finally we have
   *                    | investment
   * writeOff -> 4 comp | 10 000
   *
   * ----------------------------------------------------
   * unallocated companies - companies that were neither in exit nor in writeOff
   * | companiesNumber | investment  | exit | writeOff |
   * | 5               |  1000       |   2  |  1       | 2 exit, 1 writeOff -> calculations described before
   * | 2               |  2000       |   0  |  0       | 2 companies unallocated -> ignore
   * | 2               |  3000       |   0  |  0       | 2 companies unallocated -> inv = 2 * (1000 + 2000 + 3000)
   *
   * only last round is considered in the calculations for unallocated companies
   *
   * @param gr group data
   */
  private calculatePortfolioData(gr: GroupFS[]): void {

    this.resetMetrics();

    gr.forEach((group) => {
      group.subGroups.forEach((sb) => {
        let rInvestment = 0; // should be reset for each subgroup
        sb.roundsData.sort((a, b) => a.roundOrder - b.roundOrder);
        sb.roundsData.forEach((r, index) => {
          rInvestment += Number(r.investment);
          const companiesNumber = Number(r.companiesNumber);
          const exitCompNumber = Number(r.exitCompNumber);
          const writeOff = Number(r.writeOff);
          const proceeds = Number(r.proceeds);
          const gain = proceeds - rInvestment;

          if (exitCompNumber) {
            if (gain > 0) {
              this.atGain.updateMetrics(exitCompNumber, rInvestment, proceeds);
            } else if (gain < 0 && r.exitCompPrice) {
              this.atLoss.updateMetrics(exitCompNumber, rInvestment, proceeds);
            } else if (gain === 0) {
              this.atCost.updateMetrics(exitCompNumber, rInvestment, proceeds);
            } else if (!r.exitCompPrice) {
              this.writeOff.companiesNumber += exitCompNumber;
              this.writeOff.investment += rInvestment * exitCompNumber;
              this.writeOff.proceeds = 0;
              this.writeOff.gain = -this.writeOff.investment;
              this.writeOff.moic = 0;
            }
          }
          if (writeOff) {
            this.writeOff.companiesNumber += writeOff;
            this.writeOff.investment += rInvestment * writeOff;
            this.writeOff.proceeds = 0;
            this.writeOff.gain = -this.writeOff.investment;
            this.writeOff.moic = 0;
          }
          if ((index === sb.roundsData.length - 1) && (writeOff + exitCompNumber !== companiesNumber)) {
            const unallocatedCompanies = companiesNumber - (writeOff + exitCompNumber);
            this.writeOff.companiesNumber += unallocatedCompanies;
            this.writeOff.investment += rInvestment * unallocatedCompanies;
            this.writeOff.proceeds = 0;
            this.writeOff.gain = -this.writeOff.investment;
            this.writeOff.moic = 0;
          }
        });
      });
    });

    this.calculateGainAndMoic();
    this.calculateTotals();
  }

  private calculateTotals(): void {
    [this.atGain, this.atLoss, this.atCost, this.writeOff].forEach((metric) => {
      this.total.companiesNumber += metric.companiesNumber;
      this.total.investment += metric.investment;
      this.total.proceeds += metric.proceeds;
      this.total.gain = this.total.proceeds - this.total.investment;
      this.total.moic = this.total.investment ? this.total.proceeds / this.total.investment : 0;
    });

    this.totalsEmitterDTO.investment = this.total.investment;
    this.totalsEmitterDTO.proceeds = this.total.proceeds;

    this.totalsEmitter.emit(this.totalsEmitterDTO);
  }

  private calculateGainAndMoic(): void {
    [this.atGain, this.atLoss, this.atCost].forEach((metric) => {
      metric.calculateGain();
      metric.calculateMoic();
    });
  }

  private resetMetrics(): void {
    this.atCost = new AtCostFSPortfolioMetrics();
    this.atGain = new AtGainFSPortfolioMetrics();
    this.atLoss = new AtLossFSPortfolioMetrics();
    this.writeOff = new WriteOffFSPortfolioMetrics();
    this.total = new TotalFSPortfolioMetrics();
  }
}
